<template>
	<div class="login">
		<div class="flex flex-col items-center w-full gap-4">
			<div class="sign-in">
				<div class="logo">
					<div v-if="loginImage" v-html="loginImage"></div>
				</div>
				<button type="button" :disabled="loggingIn" id="btnLogin" class="btn loginButton" @click="loginPopUp()">
					{{ loggingIn || accessToken ? "Logging you in..." : "Log In" }}
				</button>
				<div class="loginTextColor">{{ versionText }}</div>
				<div class="loginTextColor" style="font-size: 0.75rem; opacity: 0.7">{{ getAuthProviderName() }}</div>
				<div
					style="margin-top: 1em; padding: 0.5em 1em; display: flex; flex-direction: column; gap: 0.25rem; align-items: center; border-radius: 0.5rem"
					v-if="environmentSettings.environmentName != 'Production'"
					class="environmentClass"
				>
					<div>{{ environmentSettings.environmentName }}</div>
					<div id="pubDate">Published:October 14, 2026<br/>build 27946</div>
				</div>
			</div>

			<div class="graphic">
				<div class="logo">
					<div v-if="logoImage" v-html="logoImage"></div>
				</div>
			</div>
			<div class="loginTextColor disclaimer" v-show="isDisclaimerVisible" v-html="disclaimerContent"></div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from "vue"
//import { msalClient } from "../auth/azureAd"nnpm r

//import { appAuth } from "../auth/appAuth"
import { useEnvironmentSettings } from "../composables/environmentSettings"
import Button from "primevue/button"
import { appAuth } from "../auth/appAuth"
import { useRouter } from "vue-router"
import { readPostLoginPath } from "../auth/postLoginRedirect"
//import { loginWithPopup } from "../auth/auth0routeGuards"
import { authProvider, getAuthProviderName } from "../auth/authProviderFactory"
import { useThemeManagerStore } from "@/store/themeManagerStore"
import { ThemeState } from "../../entities/NSwagGenerated"

const themeStore = useThemeManagerStore()
const defaultTemplateForLogin = ref<ThemeState>(new ThemeState())
const params = new URLSearchParams(window.location.search)

// Image display variables
const logoImage = ref<string | null>(null)
const loginImage = ref<string | null>(null)
const disclaimerContent = ref<string | null>("")
const isDisclaimerVisible = ref<boolean>(false)
// Function to create image HTML from binary data
const createImageFromBinary = (binaryData: string, contentType: string): string => {
	if (!binaryData) return ""

	// Check if binaryData is already a data URL
	if (binaryData.startsWith("data:")) {
		return `<img src="${binaryData}" alt="Image" style="max-width: 100%; max-height: 600px; height: auto; object-fit: contain; background: transparent;" class="transparent-image" />`
	}

	// Handle SVG files specially
	if (contentType === "image/svg+xml" || contentType.includes("svg")) {
		// For SVG, try to decode as text first
		try {
			const svgContent = atob(binaryData)
			// If it's valid SVG content, return it with size constraints
			if (svgContent.includes("<svg")) {
				// Add viewBox and size constraints to SVG
				const modifiedSvg = svgContent.replace(
					/<svg([^>]*)>/,
					'<svg$1 style="max-width: 100%; max-height: 600px; height: auto; width: auto; object-fit: contain;">'
				)
				return modifiedSvg
			}
		} catch (error) {
			// If decoding fails, fall back to data URL
			console.log("SVG decode failed, using data URL:", error)
		}
	}

	// Check if binaryData is already base64 encoded
	let base64Data = binaryData
	if (!binaryData.startsWith("/9j") && !binaryData.startsWith("iVBOR") && !binaryData.startsWith("R0lGOD") && !binaryData.startsWith("PHN2Zw")) {
		// If not base64, assume it's already binary and convert to base64
		try {
			base64Data = btoa(binaryData)
		} catch (error) {
			console.error("Error encoding binary data to base64:", error)
			return ""
		}
	}

	const dataUrl = `data:${contentType};base64,${base64Data}`
	return `<img src="${dataUrl}" alt="Image" style="max-width: 100%; max-height: 600px; height: auto; object-fit: contain; background: transparent;" class="transparent-image" />`
}

defaultTemplateForLogin.value = themeStore.getCurrentTheme

// Load images from binary data
if (defaultTemplateForLogin.value.templateUI?.logoImageBinary) {
	const logoContentType = defaultTemplateForLogin.value.templateUI.logoImageContentType || "image/png"
	logoImage.value = createImageFromBinary(defaultTemplateForLogin.value.templateUI.logoImageBinary, logoContentType)
}

if (defaultTemplateForLogin.value.templateUI?.loginImageBinary) {
	const loginContentType = defaultTemplateForLogin.value.templateUI.loginImageContentType || "image/png"
	loginImage.value = createImageFromBinary(defaultTemplateForLogin.value.templateUI.loginImageBinary, loginContentType)
}

if (defaultTemplateForLogin.value.templateUI?.disclaimerContent && defaultTemplateForLogin.value.templateUI?.isDiscaimerVisible) {
	disclaimerContent.value = defaultTemplateForLogin.value.templateUI.disclaimerContent
	isDisclaimerVisible.value = defaultTemplateForLogin.value.templateUI?.isDiscaimerVisible
}

let errorMessage = ""
const loggingIn = ref(false)
const user = ref()
const AuthMethod = import.meta.env.VITE_AUTH_METHOD || "Okta"
const environmentURL = import.meta.env.VITE_BASE_URL.toString()
const TagName = import.meta.env.BRANCH_NAME === undefined ? "" : import.meta.env.BRANCH_NAME.toString()
const environmentSettings = useEnvironmentSettings()

const router = useRouter()

const isAuthenticatedVar = ref(false)
const accessToken = ref("")
const userValue = ref()

onMounted(() => {
	// isAuthenticated().then((res) => {
	//   if(isAuthenticatedVar.value)
	//   {
	//     getAccessToken().then((res) => {
	//       //getWeather()
	//       //getWeather1()
	//     })
	//   }
	// })
	isAuthenticated().then((res) => {
		if (isAuthenticatedVar.value) {
			getAccessToken().then((token) => {
				appAuth.loginToAppWithOktaToken(accessToken.value).then((callback) => {
					if (callback != "ok") {
						sessionStorage.setItem("error", callback)
						loggingIn.value = false
						return false
						//logout()
					} else {
						//return true
						// Straight to the landing page: when a destination was remembered before
						// the login, the router's own guard sends this navigation on to it. Doing
						// it here as well would race with the callback screen for the same value.
						//
						// Only from the login screen itself. This runs several seconds after the
						// page was mounted, by which time the callback screen — or Okta restoring
						// the address it kept — may already have taken the reader to the request
						// they opened from an email. Pushing the landing page over that is exactly
						// what used to undo it.
						// Two conditions, because this runs a second or so after everything else
						// and must not undo it.
						//
						// If a destination is still waiting — a request or an article opened from
						// an email — go there: the same place the callback screen and Okta aim at,
						// so whichever navigation lands last lands in the right one.
						//
						// If there is none, the landing page is right only when the reader is still
						// on the login screen. Once they have been taken somewhere, the absence of
						// a destination means it was reached and dropped on arrival, and pushing
						// /app over it is precisely how the article was lost a second after being
						// displayed.
						const destination = readPostLoginPath()
						const onLoginScreen = router.currentRoute.value.path === "/" || router.currentRoute.value.path === "/login"

						if (destination) {
							router.push(destination)
						} else if (onLoginScreen) {
							router.push("/app")
						}
					}
				})
			})
		}
	})
})

const isAuthenticated = async () => {
	isAuthenticatedVar.value = await authProvider.isAuthenticated()
	return isAuthenticatedVar.value
}

// Injected at build time from the release tag (version:x.y.z) by the deployment pipeline.
const versionText = computed<string>(() => {
	const appVersion = import.meta.env.VITE_APP_VERSION?.toString().trim()
	return appVersion ? `Version ${appVersion}` : ""
})

const getAccessToken = async () => {
	accessToken.value = (await authProvider.getAccessToken()) || ""
	localStorage.setItem("token", accessToken.value)
	return accessToken.value
}

const loginPopUp = async function () {
	loggingIn.value = true
	authProvider.signInWithRedirect().then(async () => {})
	// loggingIn.value = true
	// loginWithPopup(auth0).then(
	// 	(loginSuccess) => {
	// 		if (loginSuccess) {
	// 			router.push("/app")
	// 		} else {
	// 			loggingIn.value = false
	// 		}
	// 	},
	// 	(error) => {
	// 		loggingIn.value = false
	// 	}
	// )
	// loginWithOkta(oktaAuth).then(
	// 	(loginSuccess) => {
	// 		if (loginSuccess) {
	// 			router.push("/app")
	// 		} else {
	// 			loggingIn.value = false
	// 		}
	// 	},
	// 	(error) => {
	// 		loggingIn.value = false
	// 	}
	// )
}
</script>

<style scoped>
.loginTextColor {
	color: var(--p-login-screen-login-text-color);
}
.loginButton {
	background-color: var(--p-login-screen-login-button-background-color);
	color: var(--p-login-screen-login-button-text-color);
	padding: 1em 2em;
	margin: 1em;
	font-size: 18px;
	display: inline-flex;
	flex-direction: row;
	align-items: center;
	border-radius: 0.75rem;
	font-weight: 500;
	text-transform: uppercase;
	box-shadow: none;
	border: none;
}
.loginButton:not(:disabled):hover {
	background-color: var(--p-login-screen-login-button-background-color-hover);
	color: var(--p-login-screen-login-button-text-color);
	padding: 1em 2em;
	margin: 1em;
	font-size: 18px;
	display: inline-flex;
	flex-direction: row;
	align-items: center;
	border-radius: 0.75rem;
	font-weight: 500;
	text-transform: uppercase;
	box-shadow: none;
	border: none;
}
.layoutWeb {
	display: grid;
	grid-template-columns: auto minmax(300px, 25%);
}

.layoutPhone {
	display: grid;
}

/* .loginButton {
	padding: 1em 2em;
	margin: 1em;
	background: var(--secondary);
	font-size: 18px;
	display: inline-flex;
	flex-direction: row;
	align-items: center;
	border-radius: 0.75rem;
	font-weight: 500;
	text-transform: uppercase;
	color: #fff;
	box-shadow: none;
	border: none;
} */

.login {
	display: flex;
	gap: 0em;
	flex-direction: row-reverse;
	align-items: center;
	align-content: center;
	padding: 0;
	margin: 0 auto;
	min-height: 100vh;
	background-color: var(--p-login-screen-login-background-color);
}

.sign-in {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	text-align: center;
	margin: 0 auto;
	padding: 2em;
	gap: 1em;
	max-width: 30rem;
	min-width: 20rem;
	border-radius: 2em;
	background-color: var(--p-login-screen-login-signin-color);
	z-index: 1;
}

.disclaimer {
	color: var(--p-login-screen-login-text-color);
	min-width: 40vw;
	max-width: 80vw;
	text-align: center;
	border-radius: 2em;
	background-color: var(--p-login-screen-login-signin-color);
	z-index: 1;
	padding: 1rem 2rem;
}

.logo img {
	margin: 0;
	max-width: 900px;
	max-height: 600px;
	height: auto;
	width: auto;
	object-fit: contain;
}

.sign-in .logo img {
	max-width: 600px;
	max-height: 450px;
	height: auto;
	width: auto;
	object-fit: contain;
}

.graphic .logo img {
	max-width: 1050px;
	max-height: 750px;
	height: auto;
	width: auto;
	object-fit: contain;
}

/* Styles for transparent images */
.transparent-image {
	background: transparent !important;
	object-fit: contain;
	box-shadow: none;
	border: none;
}

/* SVG specific styles */
.logo svg,
.sign-in .logo svg,
.graphic .logo svg {
	max-width: 100%;
	max-height: 600px;
	height: auto;
	width: auto;
}

.sign-in .logo svg {
	max-height: 450px;
}

.graphic .logo svg {
	max-height: 750px;
}

/* Container styles to support transparency */
.logo {
	background: transparent !important;
	background-image: none !important;
	background-color: transparent !important;
}

.graphic .logo {
	background: transparent !important;
	background-image: none !important;
	background-color: transparent !important;
}

.sign-in .logo {
	background: transparent !important;
}

.graphic {
	position: absolute;
	z-index: 0;
	margin: 3em;
	top: 2em;
	left: 2em;
	width: 20%;
	width: 500px;
}

@media only screen and (max-width: 768px) {
	.login {
		gap: 0em;
	}

	.sign-in {
		margin: 1em;
	}

	.graphic {
		height: 50vh;
	}
}

@media only screen and (max-width: 600px) {
	.graphic {
		display: none;
	}

	.sign-in {
		margin: 1em;
	}

	.logo img,
	.logo svg {
		max-width: 750px;
		max-height: 360px;
		width: auto;
		height: auto;
		object-fit: contain;
	}

	.gcdLogo {
		width: 10em;
		font-size: large;
	}

	.gcdLogo image {
		width: 5em;
		min-width: 50px;
	}
}

@media only screen and (max-height: 400px) {
	.graphic {
		display: none;
	}
}

.environmentClass {
	background: v-bind(environmentSettings.backgroundColor);
	color: v-bind(environmentSettings.textColor);
	padding: 0 1em;
	font-size: medium;
}
</style>
